import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Search, Phone, Mail, MessageCircle, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const defaultFaqs = [
  { question: 'Onarım ne kadar sürer?', answer: 'Çoğu onarım aynı gün içinde tamamlanır.' },
  { question: 'Orijinal parça kullanıyor musunuz?', answer: 'Evet, hem orijinal hem yan sanayi seçeneği sunuyoruz.' },
  { question: 'Kargo ile gönderebilir miyim?', answer: 'Evet, Türkiye geneli ücretsiz kargo hizmeti var.' },
];

const SupportPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [faqs, setFaqs] = useState(defaultFaqs);
  const faqRef = useRef(null);

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const res = await fetch(`${API_URL}/api/faq`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) setFaqs(data);
      }
    } catch (e) { console.error(e); }
  };

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const scrollToFaq = () => {
    faqRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero */}
        <section className="bg-gradient-hero py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6"
              >
                <HelpCircle className="w-5 h-5" />
                <span className="font-medium">Destek Merkezi</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4"
              >
                Size Nasıl Yardımcı Olabiliriz?
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-lg text-muted-foreground mb-8"
              >
                Sıkça sorulan sorulara göz atın veya bizimle iletişime geçin
              </motion.p>
              
              {/* Search */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative max-w-xl mx-auto"
              >
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Soru ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg rounded-full"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="py-12 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Phone, title: 'Bizi Arayın', desc: '+90 232 364 69 83', href: 'tel:+902323646983' },
                { icon: MessageCircle, title: 'WhatsApp', desc: 'Hızlı destek', href: 'https://wa.me/902323646983' },
                { icon: Mail, title: 'E-posta', desc: 'teslaprotr@gmail.com', href: 'mailto:teslaprotr@gmail.com' },
              ].map((item, i) => (
                <motion.a
                  key={i}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * i }}
                  className="bg-card rounded-2xl border shadow-card p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section ref={faqRef} className="py-16 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold font-display text-foreground mb-4">
                Sıkça Sorulan Sorular
              </h2>
              <p className="text-muted-foreground">
                En çok merak edilen sorular ve cevapları
              </p>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-card rounded-xl border shadow-sm px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-medium text-foreground">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Aramanızla eşleşen soru bulunamadı.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">Hala sorunuz mu var?</h2>
            <p className="text-muted-foreground mb-6">
              Size yardımcı olmaktan mutluluk duyarız
            </p>
            <Button asChild size="lg">
              <Link to="/iletisim">İletişime Geçin</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SupportPage;
